import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, OnInit, OnDestroy, ChangeDetectorRef, Component } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule, ActivatedRoute, Router, NavigationEnd, Event as RouterEvent, Scroll } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription, Observable, filter, map, startWith } from 'rxjs';
import { AppState } from 'src/app/state/app.state';
import { EditComponent } from '@workspaces/utils/edit-component';
import { WorkspaceTaskService } from '@workspaces/services/task.service';
import { TaskListFetch, taskListFeature } from '@workspaces/store/task-list.feature';
import { MatDialog } from '@angular/material/dialog';
import { TaskDetailsDialogComponent } from '../task-details/dialog/task-details-dialog.component';
import { MatButtonModule } from '@angular/material/button';
import { WorkspaceFetch, workspaceListFeature } from '@workspaces/store/workspace-list.feature';
import { WorkspaceAuthService } from '@workspaces/utils/workspace-auth.service';
import { RequiredRoleDirective } from '@workspaces/utils/workspace-guard.directive';

export type TaskViewType = 'kanban' | 'list';
@Component({
  selector: 'app-task-view',
  templateUrl: './task-view.component.html',
  styleUrls: ['./task-view.component.scss', '../../global-workspace-styles.scss'],
  imports: [
    MatIcon,
    CommonModule,
    MatProgressSpinner,
    MatTooltipModule,
    RouterModule,
    MatButtonModule,
    RequiredRoleDirective
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskViewComponent extends EditComponent implements OnInit, OnDestroy {
  public activeView: TaskViewType = 'list';
  private readonly sub = new Subscription();
  public loading$!: Observable<boolean>;
  public isDisabledEdit$!: Observable<boolean>;
  constructor(
    protected override readonly route: ActivatedRoute,
    protected readonly workspaceTaskService: WorkspaceTaskService,
    protected override readonly snackbar: MatSnackBar,
    protected override readonly router: Router,
    private readonly store: Store<AppState>,
    private readonly cdr: ChangeDetectorRef,
    private readonly dialog: MatDialog,
    private readonly workspaceAuthService: WorkspaceAuthService
  ) {
    super(route, snackbar, router);
  }

  override ngOnInit() {
    super.ngOnInit();
    this.store.dispatch(TaskListFetch({ workspaceId: this.workspaceId }));
    this.store.dispatch(WorkspaceFetch({ workspaceId: this.workspaceId }));

    this.isDisabledEdit$ = this.store.select(workspaceListFeature.workspaceById(this.workspaceId)).pipe(
      map(workspace => !!workspace?.is_archived || !!workspace?.is_deleted),
      startWith(false)
    );
    this.loading$ = this.store.select(taskListFeature.taskListIsLoading);
    this.sub.add(
      this.router.events
        .pipe(
          map(event => (event instanceof NavigationEnd ? event : (event as Scroll).routerEvent)),
          filter((event: RouterEvent): event is NavigationEnd => event instanceof NavigationEnd)
        )
        .subscribe((event: NavigationEnd) => {
          if (event.urlAfterRedirects.endsWith('list')) {
            this.activeView = 'list';
          } else if (event.urlAfterRedirects.endsWith('kanban')) {
            this.activeView = 'kanban';
          }
          this.cdr.markForCheck();
        })
    );
  }
  public addNewTask(): void {
    const dialogRef = this.dialog.open(TaskDetailsDialogComponent, {
      width: TaskDetailsDialogComponent.width,
      data: { columnId: 'todo', workspaceId: this.workspaceId }
    });

    this.sub.add(
      dialogRef.afterClosed().subscribe(() => {
        this.store.dispatch(TaskListFetch({ workspaceId: this.workspaceId }));
      })
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
